/**
 * The range sheet.
 *
 * A day of sessions with squads on them, which is not the same shape as
 * a tee sheet or a court sheet and should not be drawn like one. What
 * the desk is asked on the telephone is "is there room in the ten
 * o'clock", so the sheet is a list of times with how many places are
 * left, and the squad is underneath rather than beside.
 *
 * Two things appear here that appear on no other sheet in the product.
 *
 * The first is a refusal. Everywhere else, adding somebody to a booking
 * either works or is a mistake; here it can be a rule, and the rule is
 * about the person rather than the slot. So the search for somebody to
 * put on a squad carries the verdict with each name, and the reason is
 * shown before anybody clicks — a refusal that arrives after the squad
 * is written down is a refusal delivered on the firing line.
 *
 * The second is the golf course. A session that overlooks a fairway can
 * take tee times off somebody else's sheet, and that is a big enough
 * thing to do that it gets its own panel, shows exactly which times
 * would go, and is never the side effect of anything else.
 */

import { Fragment, useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { BylawNotices, Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { clubToday, dayLabel, firstVisible, shiftDate, hhmm } from '../lib/sheet.ts';
import { money } from '../lib/format.ts';

/* Shapes — mirroring the API's JSON one for one. */

interface Sport {
  id: string;
  code: string;
  name: string;
  requires_waiver: boolean;
  minimum_age: number | null;
  supervised_age: number | null;
  fields_available: number;
}

interface Shooter {
  shooter_id: string;
  position: number;
  shooter_kind: string;
  person_id: string | null;
  name: string;
  sponsor_name: string | null;
  needs_supervision: boolean;
  supervised_by: string | null;
  bill_to_account_id?: string | null;
}

interface Signup {
  signup_id: string;
  session_id: string;
  status: string;
  checked_in_at: string | null;
  shooters: Shooter[];
}

interface Session {
  session_id: string;
  field_id: string;
  field_name: string;
  layout: string;
  sport_id: string;
  sport_name: string;
  session_date: string;
  starts_at: string;
  session_minutes: number;
  squad_size: number;
  session_status: string;
  session_note: string | null;
  day_type: string;
  shooters_booked: number;
  places_left: number;
  parties: number;
  tee_times_held: number | null;
  tee_times_exposed: number | null;
  holes_published: number | null;
  signups: Signup[];
}

interface Sheet {
  date: string;
  sessions: Session[];
}

interface PersonHit {
  person_id: string;
  label: string;
  membership_id: string | null;
  member_account_id: string | null;
  account_number: string | null;
  role: string | null;
  may_shoot: boolean;
  needs_supervision: boolean;
  reason: string | null;
}

const KIND_LABEL: Record<string, string> = {
  member: 'Member',
  member_guest: 'Guest',
  outside: 'Outside play',
};

/* ------------------------------------------------------------------ */

export function RangeSheetPage() {
  const { atLeast, currency, club } = useSession();
  const isStaff = atLeast('staff');
  const canOpenSheet = atLeast('manager');
  const zone = club?.timezone;

  const [date, setDate] = useState(() => clubToday(zone));
  const [wholeDay, setWholeDay] = useState(false);
  useEffect(() => setWholeDay(false), [date]);
  const [sportId, setSportId] = useState<string | null>(null);
  const [signingUp, setSigningUp] = useState<Session | null>(null);
  const [golfFor, setGolfFor] = useState<Session | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const sports = useApi<{ sports: Sport[] }>('/v1/shooting/sports');
  const sport = sports.data?.sports.find((s) => s.id === sportId) ?? sports.data?.sports[0] ?? null;

  const sheet = useApi<Sheet>(
    sport ? `/v1/shooting/sheet?date=${date}&sport_id=${sport.id}` : null,
    [date, sport?.id],
  );

  const openWeek = useMutation(async () => {
    const result = await api<{ total_sessions: number; note?: string }>(
      '/v1/shooting/sheet/open',
      {
        method: 'POST',
        body: { sport_id: sport!.id, from_date: date, through_date: shiftDate(date, 6) },
      },
    );
    setNote(
      result.total_sessions === 0
        ? result.note ?? 'Nothing changed.'
        : `Opened ${result.total_sessions} sessions across the next seven days.`,
    );
    sheet.reload();
  });

  const sessions = sheet.data?.sessions ?? [];

  const cut = firstVisible(
    sessions.map((s) => ({ startsAt: s.starts_at })),
    date,
    zone,
    wholeDay,
  );
  const from = cut < 0 ? 0 : cut;
  const visible = from > 0 ? sessions.slice(from) : sessions;

  return (
    <>
      <Card
        title="Range Sheet"
        actions={
          <div className="actions">
            <button className="secondary small" onClick={() => setDate(shiftDate(date, -1))}>
              ‹ Previous
            </button>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <button className="secondary small" onClick={() => setDate(shiftDate(date, 1))}>
              Next ›
            </button>
            {date === clubToday(zone) && (
              <label className="sheet-toggle">
                <input
                  type="checkbox"
                  checked={wholeDay}
                  onChange={(e) => setWholeDay(e.target.checked)}
                />
                <span>From the start of the day</span>
              </label>
            )}
            {canOpenSheet && sport && (
              <button className="gold small" disabled={openWeek.busy} onClick={() => openWeek.run()}>
                {openWeek.busy ? 'Working…' : 'Open the week'}
              </button>
            )}
          </div>
        }
      >
        {/* The weekday, because a season runs on some days and not
            others and "2026-09-05" does not say which one this is. */}
        <div className="muted small" style={{ marginBottom: 8 }}>
          <strong>{dayLabel(date, zone)}</strong>
        </div>

        <div className="row">
          {(sports.data?.sports ?? []).map((s) => (
            <button
              key={s.id}
              className={s.id === sport?.id ? 'gold small' : 'secondary small'}
              onClick={() => setSportId(s.id)}
            >
              {s.name}
            </button>
          ))}
          <div className="spacer" />
          {sport && (
            <span className="muted small">
              {sport.minimum_age
                ? `${sport.minimum_age} and over` +
                  (sport.supervised_age
                    ? `, unaccompanied from ${sport.supervised_age}`
                    : '')
                : 'No age limit set'}
              {sport.requires_waiver ? ' · waiver required' : ''}
            </span>
          )}
        </div>
      </Card>

      {/* Whatever the club wrote down about signing up to shoot. */}
      <BylawNotices surface="shooting.booking" />

      <ErrorBanner error={sheet.error ?? sports.error} />
      <ErrorBanner error={openWeek.error} />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {golfFor && (
        <GolfPanel
          session={golfFor}
          onClose={() => setGolfFor(null)}
          onDone={(m) => {
            setNote(m);
            sheet.reload();
          }}
        />
      )}

      <Card flush>
        {sheet.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={6} />
          </div>
        ) : sessions.length === 0 ? (
          <Empty>
            Nothing on the sheet for this day. Either the season does not run today, or the
            sheet has not been opened yet.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Time</th>
                <th style={{ width: 160 }}>Field</th>
                <th>Squad</th>
                <th style={{ width: 130 }}>Places</th>
                <th style={{ width: 240 }} />
              </tr>
            </thead>
            <tbody>
              {from > 0 && (
                <tr className="sheet-earlier">
                  <td colSpan={5}>
                    <button className="linkish" onClick={() => setWholeDay(true)}>
                      Show the {from} earlier session{from === 1 ? '' : 's'}
                    </button>
                  </td>
                </tr>
              )}
              {visible.map((s) => (
                <Fragment key={s.session_id}>
                  <SessionRow
                    session={s}
                    currency={currency}
                    isStaff={isStaff}
                    signingUp={signingUp?.session_id === s.session_id}
                    onSignUp={() =>
                      setSigningUp((o) => (o?.session_id === s.session_id ? null : s))
                    }
                    onGolf={() => setGolfFor(s)}
                    onChanged={(m) => {
                      setNote(m ?? null);
                      sheet.reload();
                    }}
                  />
                  {/* Under the session it is for, rather than above the
                      sheet: signing up for the two o'clock squad used to
                      scroll the sheet away and leave somebody typing
                      names with no idea which session they were on. */}
                  {signingUp?.session_id === s.session_id && sport && (
                    <tr className="sheet-form">
                      <td colSpan={5}>
                        <SignUpForm
                          session={s}
                          sport={sport}
                          onCancel={() => setSigningUp(null)}
                          onDone={(m) => {
                            setSigningUp(null);
                            setNote(m);
                            sheet.reload();
                          }}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function SessionRow({
  session,
  currency,
  isStaff,
  signingUp,
  onSignUp,
  onGolf,
  onChanged,
}: {
  session: Session;
  currency: { code: string; symbol: string | null; exponent: number };
  isStaff: boolean;
  /** True while this row's sign-up form is the one that is open. */
  signingUp: boolean;
  onSignUp: () => void;
  onGolf: () => void;
  onChanged: (message?: string) => void;
}) {
  const { atLeast } = useSession();

  // Three ways a session is not an ordinary one, and they are not the
  // same thing. Blocked is weather or a trap down. Event means a
  // registered shoot owns it and the field is entered against the shoot.
  // Held is the range officer keeping it back — still bookable, but
  // deliberately.
  const blocked = session.session_status === 'blocked';
  const owned = session.session_status === 'event';
  const held = session.session_status === 'held';
  const bookable = !blocked && !owned;
  const holdsGolf = (session.tee_times_held ?? 0) > 0;

  const checkIn = useMutation(async (signupId: string) => {
    const result = await api<{
      total_posted_minor: number;
      take_at_the_desk: { shooter_name: string; item: string; amount_minor: number }[] | null;
    }>(`/v1/shooting/signups/${signupId}/check-in`, { method: 'POST' });

    onChanged(
      result.take_at_the_desk
        ? `Checked in and charged ${money(result.total_posted_minor, currency)}. Take at the ` +
          `desk: ${result.take_at_the_desk
            .map((t) => `${t.shooter_name} ${money(t.amount_minor, currency)}`)
            .join('; ')}.`
        : result.total_posted_minor > 0
          ? `Checked in and charged ${money(result.total_posted_minor, currency)}.`
          : 'Checked in. Nothing to charge.',
    );
  });

  const cancel = useMutation(async (signupId: string) => {
    const r = await api<{ note?: string }>(`/v1/shooting/signups/${signupId}/cancel`, {
      method: 'POST',
    });
    onChanged(r.note ?? 'Taken off the sheet.');
  });

  const block = useMutation(async () => {
    await api(`/v1/shooting/sessions/${session.session_id}`, {
      method: 'PATCH',
      body: blocked
        ? { status: 'open', note: null }
        : { status: 'blocked', note: 'Blocked at the desk' },
    });
    onChanged();
  });

  return (
    <>
      <tr style={blocked || owned ? { opacity: 0.55 } : undefined}>
        <td className="mono">{hhmm(session.starts_at)}</td>
        <td>
          {session.field_name}
          <div className="muted small">{session.layout.replace('_', ' ')}</div>
        </td>
        <td>
          {blocked ? (
            <Status tone="serious">
              Blocked{session.session_note ? ` — ${session.session_note}` : ''}
            </Status>
          ) : owned ? (
            <Status tone="neutral">A registered shoot has this</Status>
          ) : session.signups.length === 0 ? (
            <span className="muted small">—</span>
          ) : (
            session.signups.map((su) => (
              <div key={su.signup_id} style={{ marginBottom: 4 }}>
                {su.shooters.map((sh) => (
                  <span key={sh.shooter_id} style={{ display: 'inline-block', marginRight: 10 }}>
                    {sh.name}
                    {sh.shooter_kind === 'member_guest' && (
                      <span className="muted small"> (guest of {sh.sponsor_name ?? 'a member'})</span>
                    )}
                    {sh.shooter_kind === 'outside' && (
                      <span className="muted small"> (outside play)</span>
                    )}
                    {/* The one thing on this row that is a safety matter
                        rather than a booking detail, so it is not
                        abbreviated and not left to a color. */}
                    {sh.needs_supervision && (
                      <span className="muted small"> · with {sh.supervised_by ?? 'an adult'}</span>
                    )}
                  </span>
                ))}
                {su.status === 'checked_in' && <span className="muted small"> · checked in</span>}
              </div>
            ))
          )}
        </td>
        <td>
          {blocked || owned ? (
            <span className="muted small">—</span>
          ) : session.places_left === 0 ? (
            <Status tone="neutral">Full</Status>
          ) : (
            <Status tone="good">
              {session.places_left} of {session.squad_size}
            </Status>
          )}
        </td>
        <td className="actions">
          {bookable && session.places_left > 0 && (
            <button
              className={signingUp ? 'small' : 'secondary small'}
              onClick={onSignUp}
              aria-expanded={signingUp}
            >
              {signingUp ? 'Close' : held ? 'Sign up (held)' : 'Sign up'}
            </button>
          )}
          {isStaff &&
            session.signups
              .filter((su) => su.status === 'booked')
              .map((su) => (
                <span key={su.signup_id}>
                  <button className="small" disabled={checkIn.busy} onClick={() => checkIn.run(su.signup_id)}>
                    Check in
                  </button>
                  <button
                    className="secondary small"
                    disabled={cancel.busy}
                    onClick={() => cancel.run(su.signup_id)}
                  >
                    Cancel
                  </button>
                </span>
              ))}
          {/* Only where the range and the course actually meet. A button
              on every session would be a button that does nothing on
              most of them. */}
          {isStaff && ((session.tee_times_exposed ?? 0) > 0 || holdsGolf) && (
            <button className={holdsGolf ? 'small' : 'secondary small'} onClick={onGolf}>
              {holdsGolf ? `Holding ${session.tee_times_held} tee times` : 'Golf'}
            </button>
          )}
          {/* Not offered while the session is holding tee times: the
              course would stay shut for a session nobody is running.
              Hand the sheet back first. */}
          {atLeast('manager') && !owned && !holdsGolf && session.signups.length === 0 && (
            <button className="secondary small" disabled={block.busy} onClick={() => block.run()}>
              {blocked ? 'Unblock' : 'Block'}
            </button>
          )}
        </td>
      </tr>
      {(checkIn.error || block.error || cancel.error) && (
        <tr>
          <td colSpan={5}>
            <ErrorBanner error={checkIn.error ?? block.error ?? cancel.error} />
          </td>
        </tr>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */

interface Draft {
  shooter_kind: 'member' | 'member_guest' | 'outside';
  person_id?: string;
  display_name?: string;
  supervising_person_id?: string;
  label: string;
  needs_supervision: boolean;
}

function SignUpForm({
  session,
  sport,
  onDone,
  onCancel,
}: {
  session: Session;
  sport: Sport;
  onDone: (message: string) => void;
  onCancel: () => void;
}) {
  const { atLeast } = useSession();
  const isStaff = atLeast('staff');

  const [search, setSearch] = useState('');
  const [account, setAccount] = useState<PersonHit | null>(null);
  const [squad, setSquad] = useState<Draft[]>([]);
  const [guestName, setGuestName] = useState('');
  const [outsideName, setOutsideName] = useState('');

  const hits = useApi<{ people: PersonHit[] }>(
    isStaff && search.trim().length >= 2
      ? `/v1/shooting/people?sport_id=${sport.id}&search=${encodeURIComponent(search.trim())}` +
        `&date=${session.session_date}`
      : null,
    [search, sport.id, session.session_date],
  );

  // Adults already on the squad, which is what a supervised shooter can
  // be paired with. Computed here rather than asked for, because the
  // desk should not have to know which of the names they have typed
  // counts as an adult.
  const adults = squad.filter((s) => s.person_id && !s.needs_supervision);

  const sign = useMutation(async () => {
    const r = await api<{ signup: { shooters: unknown[] } }>('/v1/shooting/signups', {
      method: 'POST',
      body: {
        session_id: session.session_id,
        ...(isStaff && account?.membership_id ? { membership_id: account.membership_id } : {}),
        shooters: squad.map((s) => ({
          shooter_kind: s.shooter_kind,
          person_id: s.person_id,
          display_name: s.display_name,
          supervising_person_id: s.supervising_person_id,
        })),
      },
    });
    onDone(
      `${r.signup.shooters.length} on the ${hhmm(session.starts_at)} at ${session.field_name}.`,
    );
  });

  const add = (d: Draft) => setSquad((q) => [...q, d]);
  const remove = (i: number) => setSquad((q) => q.filter((_, n) => n !== i));

  return (
    <Card
      title={
        `${session.field_name} · ${hhmm(session.starts_at)} · ` +
        `${session.session_minutes} minutes · squad of ${session.squad_size}`
      }
      actions={
        <div className="actions">
          <button className="secondary small" onClick={onCancel}>
            Close
          </button>
        </div>
      }
    >
      <ErrorBanner error={sign.error} />

      {isStaff && (
        // Not a <label> around the results: a label forwards a click on
        // anything inside it to its own control, which swallows the
        // click on a search result and leaves the form looking broken.
        <div className="field-block">
          <span className="field-label">Add somebody to the squad</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name"
          />
          {(hits.data?.people ?? []).map((p) => (
            <button
              key={p.person_id}
              className="secondary small"
              disabled={!p.may_shoot || squad.some((s) => s.person_id === p.person_id)}
              style={{ marginTop: 4, textAlign: 'left', display: 'block', width: '100%' }}
              onClick={() => {
                if (!account && p.membership_id) setAccount(p);
                add({
                  shooter_kind: 'member',
                  person_id: p.person_id,
                  label: p.label,
                  needs_supervision: p.needs_supervision,
                });
                setSearch('');
              }}
            >
              <strong>{p.label}</strong>
              {p.account_number && <span className="muted small"> · {p.account_number}</span>}
              {/* The verdict, on the row, before anybody clicks. The
                  whole reason this search exists rather than the generic
                  member lookup. */}
              {!p.may_shoot && (
                <div className="muted small" style={{ whiteSpace: 'normal' }}>
                  {p.reason}
                </div>
              )}
              {p.may_shoot && p.needs_supervision && (
                <div className="muted small" style={{ whiteSpace: 'normal' }}>
                  {p.reason}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="grid-2">
        <div className="field-block">
          <span className="field-label">A guest</span>
          <div className="row">
            <input
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Their name"
            />
            <button
              className="secondary small"
              disabled={guestName.trim().length < 2}
              onClick={() => {
                add({
                  shooter_kind: 'member_guest',
                  display_name: guestName.trim(),
                  label: guestName.trim(),
                  needs_supervision: false,
                });
                setGuestName('');
              }}
            >
              Add
            </button>
          </div>
          <div className="hint">
            A guest is charged at the guest rate and it lands on whoever brought them.
          </div>
        </div>

        {isStaff && (
          <div className="field-block">
            <span className="field-label">Outside play</span>
            <div className="row">
              <input
                value={outsideName}
                onChange={(e) => setOutsideName(e.target.value)}
                placeholder="Their name"
              />
              <button
                className="secondary small"
                disabled={outsideName.trim().length < 2}
                onClick={() => {
                  add({
                    shooter_kind: 'outside',
                    display_name: outsideName.trim(),
                    label: outsideName.trim(),
                    needs_supervision: false,
                  });
                  setOutsideName('');
                }}
              >
                Add
              </button>
            </div>
            <div className="hint">No account. They pay at the desk.</div>
          </div>
        )}
      </div>

      {squad.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 50 }}>#</th>
              <th>Who</th>
              <th style={{ width: 130 }}>Kind</th>
              <th style={{ width: 240 }}>With</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {squad.map((s, i) => (
              <tr key={i}>
                <td className="mono">{i + 1}</td>
                <td>{s.label}</td>
                <td className="muted small">{KIND_LABEL[s.shooter_kind]}</td>
                <td>
                  {s.needs_supervision ? (
                    <select
                      value={s.supervising_person_id ?? ''}
                      onChange={(e) =>
                        setSquad((q) =>
                          q.map((row, n) =>
                            n === i ? { ...row, supervising_person_id: e.target.value } : row,
                          ),
                        )
                      }
                    >
                      <option value="">Name the adult…</option>
                      {adults.map((a) => (
                        <option key={a.person_id} value={a.person_id}>
                          {a.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="muted small">—</span>
                  )}
                </td>
                <td className="actions">
                  <button className="secondary small" onClick={() => remove(i)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {squad.some((s) => s.needs_supervision && !s.supervising_person_id) && (
        <Status tone="warning">
          Somebody on this squad needs an adult with them, and the adult has to be on the squad
          too.
        </Status>
      )}

      <div className="actions" style={{ marginTop: 12 }}>
        <button
          disabled={
            sign.busy ||
            squad.length === 0 ||
            squad.some((s) => s.needs_supervision && !s.supervising_person_id)
          }
          onClick={() => sign.run()}
        >
          {sign.busy ? 'Working…' : `Put ${squad.length || ''} on the sheet`}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

interface Exposed {
  tee_time_id: string;
  course_name: string;
  starts_at: string;
  status: string;
  note: string | null;
  groups_booked: number;
  reason: string;
}

/**
 * What this session would do to the golf course.
 *
 * Its own panel, and it shows the times before it takes them. Taking
 * seven tee times off a Saturday is a thing a person should do
 * deliberately, having read the list, and never as the consequence of
 * having clicked something else.
 */
function GolfPanel({
  session,
  onClose,
  onDone,
}: {
  session: Session;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const { atLeast } = useSession();
  const canClaim = atLeast('manager');

  const impact = useApi<{
    exposed: Exposed[];
    held: { tee_times_held: number; holes_published: number } | null;
    groups_already_booked: number;
    blocked_by: string | null;
  }>(`/v1/shooting/sessions/${session.session_id}/golf-impact`);

  const claim = useMutation(async () => {
    const r = await api<{
      tee_times_taken: number;
      holes_published: number;
      note?: string;
    }>(`/v1/shooting/sessions/${session.session_id}/claim-golf`, {
      method: 'POST',
      body: { publish_conditions: true },
    });
    onDone(
      `Took ${r.tee_times_taken} tee time(s) and closed ${r.holes_published} hole(s).` +
        (r.note ? ` ${r.note}` : ''),
    );
    impact.reload();
  });

  const release = useMutation(async () => {
    const r = await api<{ tee_times_given_back: number; holes_reopened: number }>(
      `/v1/shooting/sessions/${session.session_id}/release-golf`,
      { method: 'POST' },
    );
    onDone(
      `Gave back ${r.tee_times_given_back} tee time(s) and reopened ${r.holes_reopened} hole(s).`,
    );
    impact.reload();
  });

  const holding = (impact.data?.held?.tee_times_held ?? 0) > 0;
  const exposed = impact.data?.exposed ?? [];

  return (
    <Card
      title={`The golf course · ${session.field_name} at ${hhmm(session.starts_at)}`}
      actions={
        <div className="actions">
          <button className="secondary small" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      <ErrorBanner error={impact.error ?? claim.error ?? release.error} />

      {impact.loading ? (
        <Loading rows={3} />
      ) : exposed.length === 0 ? (
        <Empty>
          Nothing on the course is in this field's fall zone at this hour. Nothing would be
          taken.
        </Empty>
      ) : (
        <>
          <p className="muted small">
            {holding
              ? `This session is holding ${impact.data!.held!.tee_times_held} tee time(s) and ` +
                `${impact.data!.held!.holes_published} closed hole(s).`
              : `${exposed.length} group(s) would be standing in the fall zone while this ` +
                'session is hot. These are the times that would go:'}
          </p>

          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 80 }}>Time</th>
                <th style={{ width: 180 }}>Course</th>
                <th style={{ width: 120 }}>Now</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {exposed.map((e) => (
                <tr key={e.tee_time_id}>
                  <td className="mono">{hhmm(e.starts_at)}</td>
                  <td>{e.course_name}</td>
                  <td>
                    {e.groups_booked > 0 ? (
                      <Status tone="serious">{e.groups_booked} booked</Status>
                    ) : e.status === 'blocked' ? (
                      <Status tone="neutral">Blocked</Status>
                    ) : (
                      <Status tone="good">Open</Status>
                    )}
                  </td>
                  <td className="muted small">{e.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {impact.data?.blocked_by && (
            <div style={{ marginTop: 12 }}>
              <Status tone="serious">{impact.data.blocked_by}</Status>
            </div>
          )}

          {canClaim && (
            <div className="actions" style={{ marginTop: 12 }}>
              {holding ? (
                <button disabled={release.busy} onClick={() => release.run()}>
                  {release.busy ? 'Working…' : 'Give the tee times back'}
                </button>
              ) : (
                <button
                  disabled={claim.busy || (impact.data?.groups_already_booked ?? 0) > 0}
                  onClick={() => claim.run()}
                >
                  {claim.busy ? 'Working…' : `Take ${exposed.length} tee time(s) and close the holes`}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

/**
 * A member's own view: what they are signed up for, and whether their
 * waiver still stands.
 *
 * The waiver is at the top rather than buried, because it is the one
 * thing that will stop them shooting on Saturday and the one thing they
 * can fix on a Tuesday.
 */
export function MyRangePage() {
  const sports = useApi<{ sports: Sport[] }>('/v1/shooting/sports');
  const standing = useApi<{
    standing: {
      acceptance_id: string;
      sport_name: string;
      version: number;
      title: string;
      accepted_on: string;
      expires_on: string | null;
      is_current: boolean;
    }[];
  }>('/v1/shooting/waivers/standing');

  const signed = new Set((standing.data?.standing ?? []).filter((s) => s.is_current).map((s) => s.sport_name));

  return (
    <>
      <Card title="My Shooting">
        <ErrorBanner error={sports.error ?? standing.error} />

        {sports.loading || standing.loading ? (
          <Loading rows={3} />
        ) : (sports.data?.sports ?? []).length === 0 ? (
          <Empty>The club is not running any shooting sports yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Sport</th>
                <th style={{ width: 200 }}>Ages</th>
                <th style={{ width: 260 }}>My waiver</th>
              </tr>
            </thead>
            <tbody>
              {(sports.data?.sports ?? []).map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td className="muted small">
                    {s.minimum_age
                      ? `${s.minimum_age} and over` +
                        (s.supervised_age ? `, unaccompanied from ${s.supervised_age}` : '')
                      : 'No age limit'}
                  </td>
                  <td>
                    {!s.requires_waiver ? (
                      <span className="muted small">Not required</span>
                    ) : signed.has(s.name) ? (
                      <Status tone="good">Signed and current</Status>
                    ) : (
                      <Status tone="warning">Not signed — see the desk before you shoot</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="What I Have Signed" flush>
        {(standing.data?.standing ?? []).length === 0 ? (
          <Empty>Nothing yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Sport</th>
                <th>Document</th>
                <th style={{ width: 130 }}>Signed</th>
                <th style={{ width: 130 }}>Runs out</th>
                <th style={{ width: 140 }}>Standing</th>
              </tr>
            </thead>
            <tbody>
              {(standing.data?.standing ?? []).map((s) => (
                <tr key={s.acceptance_id}>
                  <td>{s.sport_name}</td>
                  <td>
                    {s.title} <span className="muted small">v{s.version}</span>
                  </td>
                  <td className="mono">{s.accepted_on}</td>
                  <td className="mono">{s.expires_on ?? '—'}</td>
                  <td>
                    {s.is_current ? (
                      <Status tone="good">Current</Status>
                    ) : (
                      <Status tone="warning">Needs signing again</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
